import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  IndianRupee, Plus, Search, Download, Trash2, Pencil, Loader2, AlertTriangle,
  ChevronLeft, ChevronRight, CheckCircle2, Banknote, Wallet, Wallet2, Sparkles, ArrowUpFromLine,
} from "lucide-react";
import { toast } from "sonner";

const formatINR = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}₹${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
};

const todayStr = () => new Date().toISOString().split("T")[0];

const emptyForm = () => ({
  vendor_id: "",
  amount: "",
  date: todayStr(),
  mode: "Bank",
  transaction_reference: "",
  purchase_id: "",
  notes: "",
});

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [openPurchases, setOpenPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Data loading ──────────────────────────────────────────────────────────
  const fetchAll = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/payments?limit=1000"),
        api.get("/vendors"),
      ]);
      setPayments(r1.data || []);
      setVendors(r2.data || []);
    } catch {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { setPage(1); }, [search, vendorFilter, modeFilter, dateFrom, dateTo, pageSize]);

  // When vendor changes in the modal, fetch their open purchases for manual allocation
  useEffect(() => {
    if (!dialogOpen || !form.vendor_id) {
      setOpenPurchases([]);
      return;
    }
    setLoadingPurchases(true);
    api.get(`/vendors/${form.vendor_id}/open-purchases`)
      .then((r) => setOpenPurchases(r.data || []))
      .catch(() => setOpenPurchases([]))
      .finally(() => setLoadingPurchases(false));
  }, [form.vendor_id, dialogOpen]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let items = payments;
    if (vendorFilter) items = items.filter((r) => r.vendor_id === vendorFilter);
    if (modeFilter) items = items.filter((r) => r.mode === modeFilter);
    if (dateFrom) items = items.filter((r) => r.date >= dateFrom);
    if (dateTo) items = items.filter((r) => r.date <= dateTo);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((r) =>
        (r.vendor_name || "").toLowerCase().includes(q) ||
        (r.transaction_reference || "").toLowerCase().includes(q) ||
        (r.notes || "").toLowerCase().includes(q) ||
        (r.allocations || []).some((a) => (a.reference_label || "").toLowerCase().includes(q))
      );
    }
    return items;
  }, [payments, vendorFilter, modeFilter, dateFrom, dateTo, search]);

  const stats = useMemo(() => {
    const total = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);
    const cash = filtered.filter((r) => r.mode === "Cash").reduce((s, r) => s + Number(r.amount || 0), 0);
    const bank = filtered.filter((r) => r.mode === "Bank").reduce((s, r) => s + Number(r.amount || 0), 0);
    const advance = filtered.reduce((s, r) => s + Number(r.advance_added || 0), 0);
    return { total, count: filtered.length, cash, bank, advance };
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const pagedPayments = filtered.slice(startIdx, startIdx + pageSize);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.payment_id);
    setForm({
      vendor_id: r.vendor_id || "",
      amount: r.amount || "",
      date: r.date || todayStr(),
      mode: r.mode || "Bank",
      transaction_reference: r.transaction_reference || "",
      purchase_id: "",
      notes: r.notes || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.vendor_id) return toast.error("Please choose a vendor");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast.error("Amount must be greater than zero");
    setSaving(true);
    try {
      const payload = {
        vendor_id: form.vendor_id,
        amount: amt,
        date: form.date,
        mode: form.mode,
        transaction_reference: form.transaction_reference || "",
        notes: form.notes || "",
      };
      if (form.purchase_id) payload.purchase_id = form.purchase_id;

      const r = editingId
        ? await api.put(`/payments/${editingId}`, payload)
        : await api.post("/payments", payload);

      const summary = r.data?.payment;
      const applied = (summary?.allocations || []).reduce((s, a) => s + Number(a.amount_applied || 0), 0);
      const advance = Number(summary?.advance_added || 0);
      const allocCount = (summary?.allocations || []).length;
      const bits = [];
      if (allocCount) bits.push(`Applied ${formatINR(applied)} across ${allocCount} purchase(s)`);
      if (advance > 0.01) bits.push(`${formatINR(advance)} → advance`);
      toast.success(
        editingId
          ? `Payment updated. ${bits.join(" · ")}`
          : `Payment saved. ${bits.join(" · ") || "No allocations matched."}`
      );
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/payments/${deleteTarget.payment_id}`);
      toast.success("Payment reversed");
      setPayments((prev) => prev.filter((r) => r.payment_id !== deleteTarget.payment_id));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reverse failed");
    } finally {
      setDeleting(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (vendorFilter) params.set("vendor_id", vendorFilter);
      if (modeFilter) params.set("mode", modeFilter);
      if (dateFrom) params.set("from_date", dateFrom);
      if (dateTo) params.set("to_date", dateTo);
      const r = await api.get(`/payments/export.csv?${params.toString()}`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `payments_${todayStr()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Payments</h1>
          <p className="text-sm text-[#64748b] mt-1">Money paid to vendors. FIFO-allocated against open purchases.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadCsv} disabled={loading || filtered.length === 0}
            data-testid="export-payments-csv-btn" className="border-[#e2e8f0] rounded-xl">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={openCreate} data-testid="record-payment-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl">
            <Plus className="h-4 w-4 mr-2" /> Record Payment
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-white border-[#e2e8f0]"><CardContent className="p-4">
          <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium flex items-center gap-1"><ArrowUpFromLine className="h-3 w-3" /> Total Paid Out</p>
          <p className="font-outfit text-xl font-bold text-[#0f172a] mt-1" data-testid="stat-total-paid">{formatINR(stats.total)}</p>
          <p className="text-[11px] text-[#94a3b8] mt-0.5">{stats.count} payment{stats.count !== 1 ? "s" : ""}</p>
        </CardContent></Card>
        <Card className="bg-white border-[#10b981]/20"><CardContent className="p-4">
          <p className="text-[10px] text-[#10b981] uppercase tracking-wider font-medium flex items-center gap-1"><Banknote className="h-3 w-3" /> Bank</p>
          <p className="font-outfit text-xl font-bold text-[#10b981] mt-1" data-testid="stat-bank">{formatINR(stats.bank)}</p>
        </CardContent></Card>
        <Card className="bg-white border-[#f59e0b]/20"><CardContent className="p-4">
          <p className="text-[10px] text-[#f59e0b] uppercase tracking-wider font-medium flex items-center gap-1"><Wallet className="h-3 w-3" /> Cash</p>
          <p className="font-outfit text-xl font-bold text-[#f59e0b] mt-1" data-testid="stat-cash">{formatINR(stats.cash)}</p>
        </CardContent></Card>
        <Card className="bg-white border-[#7c3aed]/20"><CardContent className="p-4">
          <p className="text-[10px] text-[#7c3aed] uppercase tracking-wider font-medium flex items-center gap-1"><Wallet2 className="h-3 w-3" /> Advance Sent</p>
          <p className="font-outfit text-xl font-bold text-[#7c3aed] mt-1" data-testid="stat-advance">{formatINR(stats.advance)}</p>
        </CardContent></Card>
      </div>

      {/* Filter bar */}
      <Card className="bg-white border-[#e2e8f0]"><CardContent className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendor / reference / purchase…"
              data-testid="payments-search" className="pl-9 h-9 border-[#e2e8f0]" />
          </div>
          <Select value={vendorFilter || "all"} onValueChange={(v) => setVendorFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44 h-9 border-[#e2e8f0]" data-testid="filter-vendor"><SelectValue placeholder="All vendors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All vendors</SelectItem>
              {vendors.map((v) => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={modeFilter || "all"} onValueChange={(v) => setModeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-32 h-9 border-[#e2e8f0]" data-testid="filter-mode"><SelectValue placeholder="All modes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modes</SelectItem>
              <SelectItem value="Bank">Bank</SelectItem>
              <SelectItem value="Cash">Cash</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36 h-9 border-[#e2e8f0]" data-testid="filter-from" />
          <span className="text-xs text-[#94a3b8]">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36 h-9 border-[#e2e8f0]" data-testid="filter-to" />
        </div>
      </CardContent></Card>

      {/* Table */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm"><CardContent className="p-0">
        {loading ? (
          <div className="p-12 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4338ca]" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <ArrowUpFromLine className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
            <p className="text-sm font-medium text-[#0f172a] mb-1">{payments.length === 0 ? "Record your first vendor payment" : "No payments match these filters."}</p>
            <p className="text-xs text-[#64748b] mb-4">{payments.length === 0 ? "Every payment is auto-allocated against the vendor's oldest open purchases." : "Try clearing the filters above."}</p>
            {payments.length === 0 && (
              <Button onClick={openCreate} className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl" data-testid="empty-record-btn">
                <Plus className="h-4 w-4 mr-2" /> Record Payment
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Vendor</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Mode</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Reference</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Amount</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Allocated To</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedPayments.map((r) => (
                  <tr key={r.payment_id} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`payment-row-${r.payment_id}`}>
                    <td className="py-3 px-4 text-[#64748b] text-xs">{r.date}</td>
                    <td className="py-3 px-4 text-[#0f172a] font-medium">{r.vendor_name || "-"}</td>
                    <td className="py-3 px-4">
                      <Badge className={
                        r.mode === "Bank"
                          ? "bg-[#10b981]/10 text-[#10b981] text-[10px]"
                          : "bg-[#f59e0b]/10 text-[#f59e0b] text-[10px]"
                      }>{r.mode === "Bank" ? <Banknote className="h-3 w-3 mr-1" /> : <Wallet className="h-3 w-3 mr-1" />}{r.mode}</Badge>
                    </td>
                    <td className="py-3 px-4 text-[#64748b] font-mono text-xs">{r.transaction_reference || "-"}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-[#0f172a]">{formatINR(r.amount)}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {(r.allocations || []).map((a) => (
                          <Badge key={a.purchase_id} className="bg-[#4338ca]/10 text-[#4338ca] text-[10px] font-mono" title={`Applied ${formatINR(a.amount_applied)}`}>
                            {a.purchase_status_after === "paid" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                            {(a.reference_label || a.purchase_id).slice(0, 22)}: {formatINR(a.amount_applied)}
                          </Badge>
                        ))}
                        {(r.advance_added || 0) > 0.01 && (
                          <Badge className="bg-[#7c3aed]/10 text-[#7c3aed] text-[10px]" title="Surplus held as advance with vendor">
                            <Sparkles className="h-3 w-3 mr-1" /> {formatINR(r.advance_added)} advance
                          </Badge>
                        )}
                        {(r.allocations || []).length === 0 && (r.advance_added || 0) < 0.01 && (
                          <span className="text-[10px] text-[#94a3b8]">-</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 p-0" title="Edit" data-testid={`edit-payment-${r.payment_id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(r)} className="h-7 w-7 p-0 text-[#ef4444] hover:bg-[#ef4444]/10" title="Reverse payment" data-testid={`delete-payment-${r.payment_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination bar */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#f1f5f9] bg-[#fafbfc]" data-testid="payments-pagination">
              <div className="text-xs text-[#64748b]">
                Viewing {startIdx + 1}-{Math.min(startIdx + pagedPayments.length, filtered.length)} of {filtered.length}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#94a3b8]">Rows</span>
                  <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                    <SelectTrigger className="h-7 w-[70px] border-[#e2e8f0] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="h-7 px-2 rounded-md border-[#e2e8f0] text-xs" data-testid="payments-prev">
                    <ChevronLeft className="h-3 w-3 mr-0.5" /> Previous
                  </Button>
                  <span className="text-xs text-[#64748b] px-2">Page {safePage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="h-7 px-2 rounded-md border-[#e2e8f0] text-xs" data-testid="payments-next">
                    Next <ChevronRight className="h-3 w-3 ml-0.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent></Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && !saving && setDialogOpen(false)}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg flex items-center gap-2">
              <ArrowUpFromLine className="h-4 w-4 text-[#4338ca]" /> {editingId ? "Edit Payment" : "Record Payment"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Original allocations are reversed and the new values are re-applied via FIFO."
                : "Allocates oldest purchases first. Excess becomes an advance with the vendor."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs font-medium">Vendor *</Label>
              <Select value={form.vendor_id || ""} onValueChange={(v) => setForm({ ...form, vendor_id: v, purchase_id: "" })}>
                <SelectTrigger className="mt-1 border-[#e2e8f0] h-9" data-testid="form-vendor"><SelectValue placeholder="Choose vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Amount (INR) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00" data-testid="form-amount" className="mt-1 border-[#e2e8f0] h-9 font-mono" />
              </div>
              <div>
                <Label className="text-xs font-medium">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="form-date" className="mt-1 border-[#e2e8f0] h-9" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger className="mt-1 border-[#e2e8f0] h-9" data-testid="form-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank">Bank</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">Reference</Label>
                <Input value={form.transaction_reference} onChange={(e) => setForm({ ...form, transaction_reference: e.target.value })}
                  placeholder="UTR / Cheque no" data-testid="form-ref" className="mt-1 border-[#e2e8f0] h-9 font-mono text-xs" />
              </div>
            </div>

            {/* Manual purchase allocation */}
            {form.vendor_id && (
              <div>
                <Label className="text-xs font-medium flex items-center gap-1.5">
                  Apply first to purchase (optional)
                  <span className="text-[10px] text-[#94a3b8] font-normal">— leave empty for FIFO</span>
                </Label>
                {loadingPurchases ? (
                  <div className="mt-1 h-9 flex items-center px-3 border border-[#e2e8f0] rounded-md text-xs text-[#94a3b8]">
                    <Loader2 className="h-3 w-3 animate-spin mr-2" /> Loading open purchases…
                  </div>
                ) : (
                  <Select value={form.purchase_id || "auto"} onValueChange={(v) => setForm({ ...form, purchase_id: v === "auto" ? "" : v })}>
                    <SelectTrigger className="mt-1 border-[#e2e8f0] h-9" data-testid="form-purchase">
                      <SelectValue placeholder={openPurchases.length === 0 ? "No open purchases" : "Auto (FIFO — oldest first)"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (FIFO — oldest first)</SelectItem>
                      {openPurchases.map((p) => (
                        <SelectItem key={p.purchase_id} value={p.purchase_id}>
                          {(p.reference_label || p.purchase_id).slice(0, 32)} · {p.date} · {formatINR(p.balance_due)} due
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs font-medium">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2} placeholder="Internal note (optional)" data-testid="form-notes" className="mt-1 border-[#e2e8f0]" />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}
                className="rounded-lg border-[#e2e8f0]" data-testid="cancel-payment-btn">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-lg" data-testid="save-payment-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                {editingId ? "Update Payment" : "Save Payment"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reverse confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg flex items-center gap-2 text-[#ef4444]">
              <AlertTriangle className="h-5 w-5" /> Reverse this payment?
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-4 mt-2">
              <p className="text-sm text-[#475569]">
                Reversing this payment will restore <span className="font-semibold text-[#0f172a]">{formatINR(deleteTarget.amount)}</span> to {deleteTarget.vendor_name}'s payable balance
                {(deleteTarget.advance_added || 0) > 0.01 && <> and roll back <span className="font-semibold">{formatINR(deleteTarget.advance_added)}</span> of advance with vendor</>}.
              </p>
              <div className="bg-[#fef2f2] border border-[#ef4444]/20 rounded-lg p-3 text-xs text-[#7f1d1d]">
                {(deleteTarget.allocations || []).length > 0 ? (
                  <>
                    <p className="font-semibold mb-1">Purchases that will be re-opened:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {(deleteTarget.allocations || []).map((a) => (
                        <li key={a.purchase_id}>{(a.reference_label || a.purchase_id).slice(0, 40)} ({formatINR(a.amount_applied)})</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p>This payment only contributed to advance balance. Nothing to re-open on the purchase side.</p>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}
                  className="rounded-lg border-[#e2e8f0]" data-testid="cancel-delete-payment-btn">Cancel</Button>
                <Button onClick={confirmDelete} disabled={deleting} className="bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-lg" data-testid="confirm-delete-payment-btn">
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Reverse Payment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
